/**
 * Telegram Mini App: полноэкранный режим (Bot API 8.0) и safe-area.
 *
 * Зачем модуль
 * ------------
 * 1. На телефоне/планшете мини-апп разворачивается на весь экран
 *    (`requestFullscreen`). На десктопе фуллскрин не нужен — окно клиента и без
 *    него занимает всё доступное место, а нативные кнопки там только мешают.
 * 2. В фуллскрине клиент рисует ПОВЕРХ веб-вью свой хром: системный статус-бар
 *    (часы/батарея) и плавающую пилюлю с кнопками «⋮»/«✕» в правом верхнем углу.
 *    Голый `env(safe-area-inset-*)` про этот хром не знает, поэтому фиксированные
 *    элементы приложения (таббар, колокольчик, тосты, шапки модалок) залезают под
 *    кнопки. Единственный источник правды — сам клиент:
 *      • `WebApp.safeAreaInset` — вырез/статус-бар УСТРОЙСТВА;
 *      • `WebApp.contentSafeAreaInset` — хром САМОГО Telegram внутри безопасной
 *        области.
 *    Значения складываются: полный верхний отступ = `safeAreaInset.top +
 *    contentSafeAreaInset.top` (так это показано в примерах Bot API 8.0).
 *
 * Что модуль публикует
 * --------------------
 * Инлайн-переменные на `<html>`: `--tgvp-top/-right/-bottom/-left` (px).
 * Вёрстка их напрямую НЕ читает — в `main.scss` на `:root` объявлены
 * `--miniapp-safe-t/-r/-b/-l = max(env(safe-area-inset-*), var(--tgvp-*))`.
 * Такая развязка даёт три свойства:
 *   • переменные определены ВСЕГДА (в т.ч. на обычном вебе и до первого рендера),
 *     поэтому `var()` не роняет всю декларацию в IACVT;
 *   • объявление ровно одно и живёт на `:root` — инлайн-стиль `<html>` всегда
 *     выигрывает и наследуется вниз. Объявление тех же переменных ещё и на
 *     `body` перебило бы значение из JS для всех потомков body;
 *   • вне Telegram остаётся обычный `env()`.
 *
 * Почему инсеты применяются БЕЗ гейта по `isFullscreen`
 * -----------------------------------------------------
 * `telegram-web-app.js` восстанавливает `isFullscreen` из своего `sessionStorage`
 * и этим перебивает launch-параметр. После «фуллскрин → выход → переоткрытие в
 * том же веб-вью» флаг залипает, а `fullscreenChanged` уже не приходит — гейт по
 * нему даёт либо недостачу отступа (контент под часами), либо лишнюю пустую
 * полосу. Сами инсеты приезжают отдельными событиями
 * (`safeAreaChanged`/`contentSafeAreaChanged`), и вне фуллскрина клиент отдаёт
 * нули, так что гейт не нужен: значения клиента самодостаточны и самозалечиваются.
 *
 * Единственный гейт, который тут есть, — по ПЛАТФОРМЕ: на десктопе инсеты всегда
 * нулевые, потому что фуллскрин там мы сами выключаем. Это детерминированный
 * признак, в отличие от залипающего флага.
 *
 * Док: https://core.telegram.org/bots/webapps (Bot API 8.0, 17.11.2024)
 */
import { getTelegramWebApp, isMobileDevice, isTelegramMiniApp, } from "./telegram";
const INSET_SIDES = ["top", "right", "bottom", "left"];
const CSS_VAR_BY_SIDE = {
    top: "--tgvp-top",
    right: "--tgvp-right",
    bottom: "--tgvp-bottom",
    left: "--tgvp-left",
};
/** Потолок на случай мусора от клиента: больше этого — точно баг хоста. */
const MAX_INSET_PX = 240;
/** Фуллскрин и safe-area — Bot API 8.0; управление свайпами — 7.7. */
const FULLSCREEN_MIN_VERSION = "8.0";
const SWIPES_MIN_VERSION = "7.7";
/** Платформы клиента, где просим фуллскрин. iPad репортится как `ios`. */
const MOBILE_PLATFORMS = new Set(["android", "android_x", "ios"]);
/** Платформы, где фуллскрин не нужен. */
const DESKTOP_PLATFORMS = new Set([
    "tdesktop",
    "macos",
    "linux",
    "weba",
    "webk",
    "web",
    "unigram",
]);
/**
 * События вьюпорта, после которых инсеты нужно перечитать. `viewportChanged`
 * прилетает часто (анимация раскрытия, клавиатура) — запись в DOM защищена
 * дедупликацией по значению, поэтому лишних стилевых инвалидаций нет.
 */
const VIEWPORT_EVENTS = [
    "safeAreaChanged",
    "contentSafeAreaChanged",
    "viewportChanged",
];
const BRIDGE_POLL_MS = 100;
const BRIDGE_WAIT_MS = 15000;
/**
 * Ранний `requestFullscreen` клиент иногда проглатывает (веб-вью ещё не
 * дорисован). Два ограниченных повтора, пока не пришло ни `fullscreenChanged`,
 * ни `fullscreenFailed`.
 */
const FULLSCREEN_RETRY_MS = [900, 2500];
/**
 * Одно слагаемое инсета. Клампа тут НЕТ намеренно: потолок применяется один раз
 * к сумме (`readTelegramInsets`), иначе два независимых клампа прикрывают друг
 * друга и снятие любого из них тесты не ловят.
 */
const toInsetPx = (value) => {
    const numeric = typeof value === "number"
        ? value
        : typeof value === "string"
            ? Number(value.trim())
            : Number.NaN;
    if (!Number.isFinite(numeric) || numeric <= 0) {
        return 0;
    }
    return Math.round(numeric);
};
/**
 * Есть ли мост Telegram с полноценным event-API. На обычном вебе
 * `window.Telegram.WebApp` тоже существует (скрипт подключён глобально), поэтому
 * одного наличия объекта мало.
 */
export const hasTelegramViewportBridge = () => typeof getTelegramWebApp()?.onEvent === "function";
export const isTelegramVersionAtLeast = (version) => {
    const webApp = getTelegramWebApp();
    if (typeof webApp?.isVersionAtLeast !== "function") {
        return false;
    }
    try {
        return webApp.isVersionAtLeast(version) === true;
    }
    catch {
        return false;
    }
};
/**
 * Мобилка/планшет vs десктоп. Если клиент платформу не назвал (старая версия,
 * `unknown`) — падаем на UA/тач-эвристику, а при сомнении возвращаем `unknown`
 * и не трогаем фуллскрин ни в одну сторону.
 */
export const classifyTelegramDevice = () => {
    const platform = getTelegramWebApp()?.platform;
    const normalized = typeof platform === "string" ? platform.trim().toLowerCase() : "";
    if (MOBILE_PLATFORMS.has(normalized)) {
        return "mobile";
    }
    if (DESKTOP_PLATFORMS.has(normalized)) {
        return "desktop";
    }
    return isMobileDevice() ? "mobile" : "unknown";
};
/**
 * Итоговые перекрытия по сторонам: устройство + хром Telegram, с санитайзом
 * (клиенты присылают и числа, и строки, и мусор) и клампом.
 *
 * На десктопе всегда нули: там мы сами выходим из фуллскрина
 * (`ensureTelegramFullscreenMode`), то есть клиент гарантированно ничего не
 * рисует поверх веб-вью, а окно и без того внутри системного хрома. Гейт по
 * платформе детерминированный (в отличие от залипающего `isFullscreen`),
 * поэтому здесь он безопасен — и страхует от клиента, который вернул бы
 * ненулевые инсеты вне фуллскрина.
 */
export const readTelegramInsets = () => {
    const webApp = getTelegramWebApp();
    const insets = { top: 0, right: 0, bottom: 0, left: 0 };
    if (classifyTelegramDevice() === "desktop") {
        return insets;
    }
    const safeArea = webApp?.safeAreaInset;
    const contentSafeArea = webApp?.contentSafeAreaInset;
    INSET_SIDES.forEach((side) => {
        insets[side] = Math.min(toInsetPx(safeArea?.[side]) + toInsetPx(contentSafeArea?.[side]), MAX_INSET_PX);
    });
    return insets;
};
/** Последнее записанное значение — чтобы не дёргать DOM на каждый viewportChanged. */
let lastAppliedInsets = null;
export const applyTelegramInsetVars = () => {
    const insets = readTelegramInsets();
    if (typeof document === "undefined") {
        return insets;
    }
    const previous = lastAppliedInsets;
    if (previous && INSET_SIDES.every((side) => previous[side] === insets[side])) {
        return insets;
    }
    lastAppliedInsets = insets;
    const style = document.documentElement.style;
    INSET_SIDES.forEach((side) => {
        style.setProperty(CSS_VAR_BY_SIDE[side], `${insets[side]}px`);
    });
    return insets;
};
const clearTelegramInsetVars = () => {
    lastAppliedInsets = null;
    if (typeof document === "undefined") {
        return;
    }
    const style = document.documentElement.style;
    INSET_SIDES.forEach((side) => {
        style.removeProperty(CSS_VAR_BY_SIDE[side]);
    });
};
/**
 * Отключает нативные вертикальные свайпы «свернуть/закрыть». В фуллскрине и в
 * приложении со своим скроллом они срабатывают ложно (тянешь список — клиент
 * сворачивает мини-апп). Обратно не включаем осознанно.
 */
export const disableTelegramVerticalSwipes = () => {
    const webApp = getTelegramWebApp();
    if (typeof webApp?.disableVerticalSwipes !== "function") {
        return;
    }
    if (!isTelegramVersionAtLeast(SWIPES_MIN_VERSION)) {
        return;
    }
    try {
        webApp.disableVerticalSwipes();
    }
    catch {
        /* best effort: старые клиенты игнорируют метод */
    }
};
/** Пришёл ответ клиента на запрос фуллскрина (changed или failed). */
let fullscreenSettled = false;
/** Клиент сказал UNSUPPORTED — повторять бессмысленно. */
let fullscreenUnsupported = false;
let fullscreenRetryTimers = [];
/** @returns удалось ли реально отправить запрос клиенту. */
const requestFullscreenOnce = () => {
    const webApp = getTelegramWebApp();
    if (fullscreenUnsupported ||
        typeof webApp?.requestFullscreen !== "function" ||
        !isTelegramVersionAtLeast(FULLSCREEN_MIN_VERSION)) {
        return false;
    }
    // Намеренно НЕ проверяем `isFullscreen === false` перед запросом: флаг
    // залипает (см. шапку файла). Повторный запрос в фуллскрине безвреден —
    // клиент ответит `fullscreenFailed: ALREADY_FULLSCREEN`.
    try {
        webApp.requestFullscreen();
        return true;
    }
    catch {
        return false;
    }
};
const clearFullscreenRetries = () => {
    fullscreenRetryTimers.forEach((timer) => window.clearTimeout(timer));
    fullscreenRetryTimers = [];
};
/**
 * Выход из фуллскрина на десктопе. Гейт по `isFullscreen` здесь допустим: он
 * либо отражает свежее состояние (вызов из обработчика `fullscreenChanged`),
 * либо залип в `true` — и тогда лишний `exitFullscreen` на десктопе безвреден.
 */
const exitFullscreenIfDesktop = () => {
    if (classifyTelegramDevice() !== "desktop") {
        return;
    }
    const webApp = getTelegramWebApp();
    if (webApp?.isFullscreen === true &&
        typeof webApp.exitFullscreen === "function") {
        try {
            webApp.exitFullscreen();
        }
        catch {
            /* best effort */
        }
    }
};
/**
 * Приводит режим отображения к нужному: телефон/планшет — фуллскрин, десктоп —
 * обычное окно. При `unknown` не делаем ничего.
 */
export const ensureTelegramFullscreenMode = () => {
    const device = classifyTelegramDevice();
    if (device !== "mobile") {
        exitFullscreenIfDesktop();
        return;
    }
    // Ретраи ставим, только если запрос реально ушёл: иначе на старом клиенте
    // (нет метода / версия < 8.0) мы бы завели два таймера вхолостую.
    if (!requestFullscreenOnce()) {
        return;
    }
    clearFullscreenRetries();
    fullscreenRetryTimers = FULLSCREEN_RETRY_MS.map((delay) => window.setTimeout(() => {
        // Гейта по `isFullscreen` тут нет сознательно: флаг залипает, и именно
        // «залип в true при оконном режиме» — тот случай, ради которого ретрай и
        // нужен. Признак «клиент ответил» — только реальные события.
        if (fullscreenSettled || fullscreenUnsupported) {
            return;
        }
        requestFullscreenOnce();
    }, delay));
};
/** Снимок для диагностики на реальном устройстве (DebugPage). */
export const readTelegramViewportSnapshot = () => {
    const webApp = getTelegramWebApp();
    return {
        hasBridge: hasTelegramViewportBridge(),
        platform: webApp?.platform ?? null,
        version: webApp?.version ?? null,
        deviceClass: classifyTelegramDevice(),
        isExpanded: webApp?.isExpanded ?? null,
        isFullscreen: webApp?.isFullscreen ?? null,
        isVerticalSwipesEnabled: webApp?.isVerticalSwipesEnabled ?? null,
        viewportHeight: webApp?.viewportHeight ?? null,
        viewportStableHeight: webApp?.viewportStableHeight ?? null,
        rawSafeAreaInset: webApp?.safeAreaInset ?? null,
        rawContentSafeAreaInset: webApp?.contentSafeAreaInset ?? null,
        resolvedInsets: readTelegramInsets(),
        fullscreenSettled,
        fullscreenUnsupported,
    };
};
let refCount = 0;
let stopCurrent = null;
/**
 * Мы действительно внутри клиента Telegram? На обычном вебе мост существует
 * (скрипт подключён глобально), но initData пустой, а `platform` = `unknown`,
 * поэтому одного наличия объекта мало. Проверяем оба признака: initData бывает
 * пустым при перезагрузке веб-вью, а `platform` SDK восстанавливает из
 * сохранённых launch-параметров.
 */
const isTelegramContext = () => {
    if (isTelegramMiniApp()) {
        return true;
    }
    const platform = getTelegramWebApp()?.platform;
    const normalized = typeof platform === "string" ? platform.trim().toLowerCase() : "";
    return MOBILE_PLATFORMS.has(normalized) || DESKTOP_PLATFORMS.has(normalized);
};
const start = () => {
    let disposed = false;
    let attached = false;
    let bridgePoll = null;
    let detachBridge = null;
    const handleViewportEvent = () => {
        applyTelegramInsetVars();
    };
    const handleFullscreenChanged = () => {
        fullscreenSettled = true;
        // Хост может включить фуллскрин и после нашего старта (параметр запуска,
        // меню клиента) — на десктопе выходим из него каждый раз, а не только на
        // инициализации.
        exitFullscreenIfDesktop();
        applyTelegramInsetVars();
    };
    const handleFullscreenFailed = (payload) => {
        fullscreenSettled = true;
        if (payload?.error === "UNSUPPORTED") {
            fullscreenUnsupported = true;
        }
        applyTelegramInsetVars();
    };
    const stopPolling = () => {
        if (bridgePoll !== null) {
            window.clearInterval(bridgePoll);
            bridgePoll = null;
        }
    };
    const attach = () => {
        const webApp = getTelegramWebApp();
        if (!webApp || typeof webApp.onEvent !== "function") {
            return;
        }
        attached = true;
        VIEWPORT_EVENTS.forEach((event) => {
            webApp.onEvent?.(event, handleViewportEvent);
        });
        webApp.onEvent?.("fullscreenChanged", handleFullscreenChanged);
        webApp.onEvent?.("fullscreenFailed", handleFullscreenFailed);
        window.addEventListener("orientationchange", handleViewportEvent);
        window.addEventListener("resize", handleViewportEvent);
        detachBridge = () => {
            VIEWPORT_EVENTS.forEach((event) => {
                webApp.offEvent?.(event, handleViewportEvent);
            });
            webApp.offEvent?.("fullscreenChanged", handleFullscreenChanged);
            webApp.offEvent?.("fullscreenFailed", handleFullscreenFailed);
            window.removeEventListener("orientationchange", handleViewportEvent);
            window.removeEventListener("resize", handleViewportEvent);
        };
        disableTelegramVerticalSwipes();
        ensureTelegramFullscreenMode();
        applyTelegramInsetVars();
    };
    const deadline = Date.now() + BRIDGE_WAIT_MS;
    const tick = () => {
        if (disposed || attached) {
            return;
        }
        if (hasTelegramViewportBridge() && isTelegramContext()) {
            stopPolling();
            attach();
            return;
        }
        // Раннего выхода по `__tgBridgeSettled` тут сознательно НЕТ. Флаг ставится
        // из `onload`, то есть после синхронного исполнения моста, — по идее к этому
        // моменту `platform`/`initData` уже проставлены. Но цена ошибки в этом
        // допущении несимметрична: один неверно ранний выход делает весь фикс тихим
        // no-op на всю сессию (ни фуллскрина, ни инсетов, ни отключённых свайпов),
        // а цена ожидания — 150 тиков с парой чтений свойств. Поэтому ждём дедлайн.
        if (Date.now() > deadline) {
            stopPolling();
        }
    };
    tick();
    if (!disposed && !attached && bridgePoll === null) {
        bridgePoll = window.setInterval(tick, BRIDGE_POLL_MS);
    }
    stopCurrent = () => {
        disposed = true;
        stopPolling();
        clearFullscreenRetries();
        detachBridge?.();
        detachBridge = null;
        clearTelegramInsetVars();
        // Модульные флаги — тоже часть состояния запуска: без сброса повторный
        // start() разово дёрнул бы requestFullscreen, а оба ретрая сразу вышли бы
        // по «клиент уже ответил» из прошлой жизни.
        fullscreenSettled = false;
        fullscreenUnsupported = false;
    };
};
/**
 * Точка входа. Идемпотентна и refcount-нута: React.StrictMode монтирует эффекты
 * дважды, а подписаться может ещё и DebugPage — подписка и таймеры всё равно
 * останутся в одном экземпляре.
 */
export const initTelegramViewport = () => {
    if (typeof window === "undefined") {
        return () => { };
    }
    refCount += 1;
    if (refCount === 1) {
        start();
    }
    // Каждый disposer срабатывает ровно один раз: без этого флага двойной вызов
    // одного и того же release() увёл бы refCount ниже реального числа
    // подписчиков и снял бы safe-area у живого приложения на всю сессию.
    let released = false;
    return () => {
        if (released) {
            return;
        }
        released = true;
        refCount = Math.max(0, refCount - 1);
        if (refCount === 0) {
            stopCurrent?.();
            stopCurrent = null;
        }
    };
};
